import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BACKEND_API_BASE_URL } from '../config/app-settings.tokens';

/**
 * Attachment Service
 * Handles all attachment-related HTTP operations including upload, download, delete, replace, and move
 */
@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = `${inject(BACKEND_API_BASE_URL)}`;
  private readonly attachmentControllerRoute = '/attachment';

  /**
   * Downloads an attachment by ID
   * Returns the response with blob and headers (including Content-Disposition)
   * @param attachmentId - The ID of the attachment to download
   * @returns Observable with blob and response headers
   */
  download(attachmentId: number): Observable<any> {
    const url = `${this.apiBaseUrl}${this.attachmentControllerRoute}/download?attachmentId=${attachmentId}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' });
  }

  /**
   * Uploads a file to a folder
   * @param file - The file to upload
   * @param folderId - The target folder ID
   * @param userId - The user ID performing the upload
   * @param delegationId - Optional delegation ID
   * @returns Observable of the upload response
   */
  upload(file: File, folderId: number, userId: number, delegationId: number | null): Observable<any> {
    const formData = new FormData();
    formData.append('File', file);
    formData.append('FolderId', folderId.toString());
    formData.append('UserId', userId.toString());
    formData.append('DelegationId', (delegationId || 0).toString());

    const url = `${this.apiBaseUrl}${this.attachmentControllerRoute}/upload`;
    return this.http.post(url, formData);
  }

  /**
   * Deletes an attachment by ID
   * @param attachmentId - The ID of the attachment to delete
   * @returns Observable of the delete response
   */
  delete(attachmentId: number): Observable<any> {
    const url = `${this.apiBaseUrl}${this.attachmentControllerRoute}?attachmentId=${attachmentId}`;
    return this.http.delete(url);
  }

  /**
   * Replaces an existing attachment with a new file
   * @param attachmentId - The ID of the attachment to replace
   * @param file - The new file to upload
   * @param delegationId - Optional delegation ID
   * @returns Observable of the replace response
   */
  replace(attachmentId: number, file: File, delegationId: number | null): Observable<any> {
    const formData = new FormData();
    formData.append('AttachmentId', attachmentId.toString());
    formData.append('File', file);
    formData.append('DelegationId', (delegationId || 0).toString());

    const url = `${this.apiBaseUrl}${this.attachmentControllerRoute}/replace`;
    return this.http.patch(url, formData);
  }

  /**
   * Renames an attachment
   * @param attachmentId - The ID of the attachment to rename
   * @param newName - The new name for the attachment
   * @param delegationId - Optional delegation ID
   * @returns Observable of the rename response
   */
  rename(attachmentId: number, newName: string, delegationId: number | null): Observable<any> {
    const body = {
      attachmentId: attachmentId,
      newAttachmentName: newName,
      delegationId: delegationId || 0,
    };

    const url = `${this.apiBaseUrl}${this.attachmentControllerRoute}/rename`;
    return this.http.patch(url, body);
  }

  /**
   * Moves an attachment to a different folder
   * @param attachmentId - The ID of the attachment to move
   * @param newFolderId - The target folder ID
   * @param delegationId - Optional delegation ID
   * @returns Observable of the move response
   */
  move(attachmentId: number, newFolderId: number, delegationId: number | null): Observable<any> {
    const body = {
      attachmentId,
      newFolderId,
      delegationId: delegationId || 0,
    };

    const url = `${this.apiBaseUrl}${this.attachmentControllerRoute}/move`;
    return this.http.patch(url, body);
  }
}
